import { Card, Text, Flex, Code, IconButton, ScrollArea } from '@radix-ui/themes'
import { useState } from 'react'
import { Cross2Icon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'

interface DebugLog {
  id: string
  timestamp: Date
  type: 'info' | 'error' | 'warn'
  message: string
  data?: any
}

let debugLogs: DebugLog[] = []
let listeners: ((logs: DebugLog[]) => void)[] = []

export const debugLog = (type: DebugLog['type'], message: string, data?: any) => {
  const log: DebugLog = {
    id: Date.now().toString(),
    timestamp: new Date(),
    type,
    message,
    data
  }
  
  debugLogs = [...debugLogs, log].slice(-50) // Keep last 50 logs
  
  // Console output
  const consoleMethod = type === 'error' ? console.error : type === 'warn' ? console.warn : console.log
  consoleMethod(`[DEBUG ${type.toUpperCase()}]`, message, data)
  
  // Notify listeners
  listeners.forEach(listener => listener(debugLogs))
}

export const DebugPanel = () => {
  const [logs, setLogs] = useState<DebugLog[]>(debugLogs)
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)

  useState(() => {
    const listener = (newLogs: DebugLog[]) => setLogs(newLogs)
    listeners.push(listener)
    return () => {
      listeners = listeners.filter(l => l !== listener)
    }
  })

  if (!isOpen) {
    return (
      <IconButton
        size="3"
        variant="surface"
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000
        }}
        onClick={() => setIsOpen(true)}
      >
        🐛
      </IconButton>
    )
  }

  return (
    <Card
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: isMinimized ? 300 : 500,
        maxHeight: isMinimized ? 60 : 400,
        zIndex: 1000,
        overflow: 'hidden'
      }}
    >
      <Flex justify="between" align="center" mb="2">
        <Text size="2" weight="bold">Debug Panel</Text>
        <Flex gap="2">
          <IconButton
            size="1"
            variant="ghost"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </IconButton>
          <IconButton
            size="1"
            variant="ghost"
            onClick={() => {
              debugLogs = []
              setLogs([])
            }}
          >
            🗑️
          </IconButton>
          <IconButton
            size="1"
            variant="ghost"
            onClick={() => setIsOpen(false)}
          >
            <Cross2Icon />
          </IconButton>
        </Flex>
      </Flex>

      {!isMinimized && (
        <ScrollArea style={{ height: 300 }}>
          <Flex direction="column" gap="2">
            {logs.length === 0 ? (
              <Text size="1" color="gray">No logs yet</Text>
            ) : (
              logs.map(log => (
                <Card key={log.id} variant="surface">
                  <Flex direction="column" gap="1">
                    <Flex justify="between">
                      <Text
                        size="1"
                        color={
                          log.type === 'error' ? 'red' :
                          log.type === 'warn' ? 'orange' : 'gray'
                        }
                        weight="bold"
                      >
                        [{log.type.toUpperCase()}]
                      </Text>
                      <Text size="1" color="gray">
                        {log.timestamp.toLocaleTimeString()}
                      </Text>
                    </Flex>
                    <Text size="1">{log.message}</Text>
                    {log.data && (
                      <Code size="1" style={{ whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(log.data, null, 2)}
                      </Code>
                    )}
                  </Flex>
                </Card>
              ))
            )}
          </Flex>
        </ScrollArea>
      )}
    </Card>
  )
}